import { useState } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import styles from "../../styles/portfolioManagerDashboard.module.scss";

const RADIAN = Math.PI / 180;

function ValueLabel({ cx, cy, midAngle, innerRadius, outerRadius, payload }) {
  if (!payload?.displayValue && payload?.displayValue !== 0) return null;
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={10}
      fontWeight={700}
      fill="#fff"
    >
      {payload.displayValue}
    </text>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: "1px solid #eaebf0", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", whiteSpace: "nowrap" }}>
      <span style={{ color: "#424242" }}>{entry.name}: </span>
      <span style={{ color: payload[0].fill }}>{entry.range}%</span>
    </div>
  );
}

function GaugeChart({
  size = 200,
  showLegend = true,
  segments,
}) {
  const [hidden, setHidden] = useState(new Set());

  const toggleSegment = (name) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const visibleSegments = segments.filter((s) => !hidden.has(s.name));
  const totalValue = visibleSegments.reduce((sum, s) => sum + (Number(s.value) || 0), 0);

  const cx     = size / 2;
  const outerR = size / 2 - 24;
  const innerR = outerR - 30;
  const chartSize = size + 32;

  return (
    <div className={styles.gaugeWrapper}>
      <PieChart width={chartSize} height={chartSize} style={{ overflow: "visible" }}>
        <Pie
          data={visibleSegments.map((s) => ({ value: s.range, name: s.name, displayValue: s.value, range: s.range }))}
          cx={cx}
          cy={size / 2 + 10}
          startAngle={90}
          endAngle={-270}
          innerRadius={innerR}
          outerRadius={outerR}
          dataKey="value"
          stroke="none"
          paddingAngle={2}
          isAnimationActive={false}
          cornerRadius={4}
          labelLine={false}
          label={ValueLabel}
        >
          {visibleSegments.map((seg, i) => (
            <Cell key={i} fill={seg.color} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        {/* Center total */}
        <text x={cx + 5} y={size / 2 + 12} textAnchor="middle" dominantBaseline="central" fontSize={30} fontWeight={700} fill="#342C82">
          {totalValue}
        </text>
      </PieChart>

      {showLegend && (
        <div className={styles.gaugeLegend}>
          {segments.map((seg, i) => {
            const isHidden = hidden.has(seg.name);
            return (
              <span
                key={i}
                className={styles.gaugeLegendItem}
                onClick={() => toggleSegment(seg.name)}
                style={{ cursor: "pointer", opacity: isHidden ? 0.35 : 1, userSelect: "none" }}
              >
                <span className={styles.gaugeLegendSwatch} style={{ background: seg.color }} />
                {seg.name}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GaugeChart;
